import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Share a URL, and report whether it ended up on the clipboard.
 *
 * Two paths, because there is no single one that works everywhere: phones get
 * the native share sheet, everything else gets a copy. `navigator.share`
 * rejects when the sheet is dismissed, which is not an error worth surfacing —
 * it falls through to copying instead.
 */
export function useShare() {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clearing on unmount matters here: the confirmation sits on a 2s timer, and
  // navigating away inside that window would otherwise set state on a component
  // that no longer exists.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const share = useCallback(async (url: string, title: string) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url })
        return
      } catch {
        /* dismissed — fall through to copying */
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked; nothing useful to say */
    }
  }, [])

  return { share, copied }
}
